import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSpinner } from '@/fontawesome';
import { formatDate, formatCurrencyCentValue } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError, showSuccessToast } from '@/utils/toasts';
import type { LongTermRecommendation, LongTermAdjustement } from '@/models';

interface LongTermRecommendationAdjustementsTableProps {
  longTermTrade: LongTermRecommendation;
  onEdit: (adjustment: LongTermAdjustement) => void;
  onDelete: (adjustmentId: string) => void;
  onLongTermRecommendationUpdated: (longTermTrade: LongTermRecommendation) => void;
  showActions?: boolean;
}

export default function LongTermRecommendationAdjustementsTable({
  longTermTrade,
  onEdit,
  onDelete,
  onLongTermRecommendationUpdated,
  showActions = true
}: LongTermRecommendationAdjustementsTableProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const currency = longTermTrade.symbol?.currentData?.currency ?? 'USD';

  const handleDelete = async (adjustment: LongTermAdjustement) => {
    // Check if this is the last adjustment
    if (longTermTrade.adjustements.length === 1) {
      handleAPIError(
        new Error('Der letzte Einstieg kann nicht gelöscht werden'),
        'Fehler beim Löschen'
      );
      return;
    }

    const confirmed = window.confirm(
      `Einstieg wirklich löschen?\n\n` +
      `Preis: ${formatCurrencyCentValue(adjustment.price, currency)}\n` +
      `Zeitpunkt: ${formatDate(adjustment.timestamp)}\n\n` +
      `Diese Aktion kann nicht rückgängig gemacht werden.`
    );

    if (!confirmed) return;

    setDeletingId(adjustment.id);

    try {
      const updatedLongTermRecommendation = await APIRoutes.deleteLongTermTradeAdjustement(
        longTermTrade.id,
        adjustment.id
      );
      onLongTermRecommendationUpdated(updatedLongTermRecommendation);
      showSuccessToast('Einstieg erfolgreich gelöscht');
      onDelete(adjustment.id);
    } catch (error) {
      handleAPIError(error, 'Fehler beim Löschen des Einstiegs');
    } finally {
      setDeletingId(null);
    }
  };

  // Sort adjustments by timestamp (ascending)
  const sortedAdjustments = [...longTermTrade.adjustements].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Zeitstempel
            </th>
            <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Preis
            </th>
            {showActions && (
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Aktionen
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {sortedAdjustments.map((adjustment) => {
            const isDeleting = deletingId === adjustment.id;

            return (
              <tr key={adjustment.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {formatDate(adjustment.timestamp)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {formatCurrencyCentValue(adjustment.price, currency)}
                </td>
                {showActions && (
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm space-x-2">
                    <button
                      onClick={() => onEdit(adjustment)}
                      disabled={isDeleting}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer disabled:opacity-50"
                      title="Bearbeiten"
                    >
                      <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(adjustment)}
                      disabled={isDeleting || longTermTrade.adjustements.length === 1}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      title={longTermTrade.adjustements.length === 1 ? 'Letzter Einstieg kann nicht gelöscht werden' : 'Löschen'}
                    >
                      {isDeleting ? (
                        <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                      ) : (
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {sortedAdjustments.length === 0 && (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          Keine Einstiege vorhanden
        </div>
      )}
    </div>
  );
}
